import { computed, type MaybeRefOrGetter, readonly, toValue, type MaybeRef } from 'vue';
import { useCssVar, useMediaQuery } from '@vueuse/core';

export type BreakpointSize = 'mobile' | 'tablet' | 'desktop';

/** Default fallback value for mobile breakpoint when CSS custom property is not available */
export const DEFAULT_MOBILE_BREAKPOINT = 767;
/** Default fallback value for tablet breakpoint when CSS custom property is not available */
export const DEFAULT_TABLET_BREAKPOINT = 1023;

/**
 * Vue 3 composable for reactive breakpoint detection
 * By default, reads breakpoint values from CSS custom properties (--breakpoint-mobile, --breakpoint-tablet)
 * Falls back to hardcoded values if CSS custom properties are not available
 * Mobile: up to --breakpoint-mobile (default 767px)
 * Tablet: from --breakpoint-mobile + 1 to --breakpoint-tablet (default 768px to 1023px)
 * Desktop: from --breakpoint-tablet + 1 onwards (default 1024px+)
 */
interface UseResponsiveOptions {
  element?: MaybeRef<HTMLElement | null | undefined>;
  customProperty?: string;
}

function parseCssVarToNumber(value: string | undefined, fallback: number): number {
  const trimmed = value?.trim() ?? '';
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function getMediaQueryBreakpoint(
  mobileBreakpoint: MaybeRefOrGetter<number>,
  tabletBreakpoint: MaybeRefOrGetter<number>,
) {
  const mobileQuery = computed(() => `(max-width: ${toValue(mobileBreakpoint)}px)`);
  const tabletQuery = computed(() => `(min-width: ${toValue(mobileBreakpoint) + 1}px) and (max-width: ${toValue(tabletBreakpoint)}px)`);
  const desktopQuery = computed(() => `(min-width: ${toValue(tabletBreakpoint) + 1}px)`);

  return {
    isMobile: useMediaQuery(mobileQuery),
    isTablet: useMediaQuery(tabletQuery),
    isDesktop: useMediaQuery(desktopQuery),
    mediaQueryStrings: {
      mobile: mobileQuery,
      tablet: tabletQuery,
      desktop: desktopQuery,
    },
  };
}

/**
 * Return responsive breakpoint states
 * @param options Optional configuration to use a custom css property as the mobile breakpoint value
 * @returns Responsive breakpoint states
 */
export function useResponsive(options?: UseResponsiveOptions) {
  const { element, customProperty } = options ?? {};

  let mobileBreakpoint: MaybeRefOrGetter<number>;
  let tabletBreakpoint: MaybeRefOrGetter<number>;

  if (element && customProperty) {
    // Use custom property for mobile breakpoint (e.g., for CartSubscription)
    const cssVar = useCssVar(customProperty, element);
    mobileBreakpoint = computed(() => parseCssVarToNumber(cssVar.value, DEFAULT_MOBILE_BREAKPOINT));
    tabletBreakpoint = DEFAULT_TABLET_BREAKPOINT;
  } else {
    // Read from :root CSS custom properties by default
    const mobileCssVar = useCssVar('--breakpoint-mobile', undefined);
    const tabletCssVar = useCssVar('--breakpoint-tablet', undefined);

    mobileBreakpoint = computed(() => parseCssVarToNumber(mobileCssVar.value, DEFAULT_MOBILE_BREAKPOINT));
    tabletBreakpoint = computed(() => parseCssVarToNumber(tabletCssVar.value, DEFAULT_TABLET_BREAKPOINT));
  }

  const { isMobile, isTablet, isDesktop, mediaQueryStrings } = getMediaQueryBreakpoint(mobileBreakpoint, tabletBreakpoint);

  const isMobileOrTablet = computed(() => isMobile.value || isTablet.value);
  const isTabletOrDesktop = computed(() => isTablet.value || isDesktop.value);

  const currentBreakpoint = computed((): BreakpointSize => {
    if (isMobile.value) return 'mobile';
    if (isTablet.value) return 'tablet';
    return 'desktop';
  });

  return {
    currentBreakpoint: readonly(currentBreakpoint),
    isMobile: readonly(isMobile),
    isTablet: readonly(isTablet),
    isDesktop: readonly(isDesktop),
    isMobileOrTablet: readonly(isMobileOrTablet),
    isTabletOrDesktop: readonly(isTabletOrDesktop),
    mediaQueryStrings: readonly(mediaQueryStrings),
  };
};